'use client';

import { useDroppable } from '@dnd-kit/core';
import { AlertTriangle } from 'lucide-react';
import type { GridMatch } from '@/types/tournament.model';
import { cn } from '@/lib/utils';
import type {
  CellModel,
  GridSlotRef,
  PlaceholderLabelMap,
  TournamentBadge,
} from './grid-model';
import { capacityLabel, cellStateClasses, verdictReasonClass } from './cell-presentation';
import type { DropVerdict } from './drop-state';
import { GridMatchCard } from './GridMatchCard';
import { GridAvailableTeamsButton } from './GridAvailableTeamsButton';

interface GridCellProps {
  cell: CellModel;
  badgeOf: (tournamentId: string) => TournamentBadge;
  pendingMatchIds: Set<string>;
  /** True while an unsaved drag-and-drop draft is pending anywhere in the grid. */
  editDisabled: boolean;
  onEditMatch: (match: GridMatch) => void;
  /** matchId -> qualifier labels for its undecided bracket slots. */
  placeholderLabels: PlaceholderLabelMap;
  /** Verdict for the match currently being dragged, or `null` when nothing is dragging. */
  dragVerdict: DropVerdict | null;
  /** Opens the "who can play" list for a slot. */
  onShowAvailableTeams: (slot: GridSlotRef) => void;
}

/**
 * One cell of the weekly matrix, always droppable.
 *
 * This used to be split into a placeholder branch and a `DroppableCell`, so that `useDroppable`
 * never sat behind an early return. That split is gone along with the branch: every (date, time)
 * intersection now has a real cell, declared or not.
 */
export function GridCell({
  cell,
  badgeOf,
  pendingMatchIds,
  editDisabled,
  onEditMatch,
  placeholderLabels,
  dragVerdict,
  onShowAvailableTeams,
}: GridCellProps) {
  const { setNodeRef } = useDroppable({ id: cell.key });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[96px] space-y-1.5 rounded-md border p-2',
        cellStateClasses(cell, dragVerdict),
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <div
          className={cn(
            'flex items-center gap-1 text-[11px] font-medium',
            cell.overbooked ? 'text-red-700' : 'text-gray-500',
          )}
        >
          {cell.overbooked && <AlertTriangle className="h-3 w-3" />}
          {capacityLabel(cell)}
        </div>
        <GridAvailableTeamsButton
          date={cell.date}
          time={cell.startTime}
          onShowAvailableTeams={onShowAvailableTeams}
        />
      </div>

      {dragVerdict?.reason && (
        <p className={cn('text-[11px]', verdictReasonClass(dragVerdict))}>
          {dragVerdict.reason}
        </p>
      )}

      {cell.matches.map((match) => {
        const badge = badgeOf(match.tournamentId);
        return (
          <GridMatchCard
            key={match.matchId}
            match={match}
            tournamentName={badge.name}
            colorIndex={badge.colorIndex}
            date={cell.date}
            time={cell.startTime}
            pending={pendingMatchIds.has(match.matchId)}
            editDisabled={editDisabled}
            placeholders={placeholderLabels.get(match.matchId)}
            onEdit={onEditMatch}
          />
        );
      })}
    </div>
  );
}
